import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { ProjectModal } from "@/components/ProjectModal";
import { PROJECTS } from "@/data/content";
import { ArrowUpRight, Github } from "lucide-react";

const ProjectCard = ({ project, onOpen, wide }) => (
    <article
        data-testid={`project-card-${project.id}`}
        className={`group flex h-full flex-col border border-white/10 bg-surface/70 transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-neon/40 hover:shadow-[0_0_32px_rgba(0,255,102,0.09)] ${wide ? "md:col-span-2" : ""}`}
    >
        <div className="flex items-center justify-between border-b border-white/5 bg-black/40 px-5 py-3">
            <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                <span className="h-2 w-2 rounded-full bg-zinc-700" />
                <span className="h-2 w-2 rounded-full bg-neon/60" />
            </div>
            <span className="font-mono2 text-[11px] text-zinc-600">
                <span className="text-neon/70">~/</span>{project.slug}
            </span>
        </div>

        <div className="flex flex-1 flex-col p-6 sm:p-7">
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="font-display text-3xl font-black tracking-tighter text-white/10 transition-colors duration-500 group-hover:text-neon/25">
                    {project.id}
                </span>
                <span className="border border-cyber/40 bg-cyber/5 px-2.5 py-1 font-mono2 text-[11px] text-cyber">
                    {project.type}
                </span>
            </div>

            <h3 className="font-display text-xl font-bold tracking-tight text-zinc-100 sm:text-2xl">
                {project.title}
            </h3>

            <div className="mt-3 flex flex-wrap gap-2">
                {project.tech.map((t) => (
                    <span key={t} className="font-mono2 text-[11px] text-zinc-500">
                        [{t}]
                    </span>
                ))}
            </div>

            <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-400">
                {project.description}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
                {project.concepts.slice(0, 4).map((c) => (
                    <span key={c} className="border border-white/10 bg-black/30 px-2.5 py-1 font-mono2 text-[11px] text-zinc-400">
                        {c}
                    </span>
                ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    data-testid={`project-view-details-${project.id}`}
                    onClick={() => onOpen(project)}
                    className="group/btn flex items-center gap-2 border border-neon/60 px-4 py-2.5 font-mono2 text-xs text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
                >
                    View Details
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
                <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`project-github-${project.id}`}
                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono2 text-xs text-zinc-300 transition-colors duration-300 hover:border-neon/60 hover:text-neon"
                >
                    <Github size={13} strokeWidth={1.5} />
                    GitHub
                </a>
            </div>
        </div>
    </article>
);

export const Projects = () => {
    const [active, setActive] = useState(null);

    return (
        <section id="projects" data-testid="projects-section" className="scroll-mt-20 py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeader
                    chapter="03"
                    kicker="Work"
                    title="Projects & Technical Work"
                    subtitle="Console-based applications and hands-on cybersecurity learning projects demonstrating programming, databases, authentication, and security fundamentals."
                    testId="projects-heading"
                />

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {PROJECTS.map((project, i) => (
                        <Reveal key={project.id} delay={(i % 2) * 0.1} className={i === PROJECTS.length - 1 ? "md:col-span-2" : ""}>
                            <ProjectCard project={project} onOpen={setActive} wide={false} />
                        </Reveal>
                    ))}
                </div>
            </div>

            <ProjectModal project={active} onClose={() => setActive(null)} />
        </section>
    );
};
